import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { NAV_LINKS, SOCIAL_LINKS, CONTACT_INFO } from '../utils/constants';
import Button from './Button';
import { newsletterAPI } from '../utils/api';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setErrorMessage('Email er påkrævet');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setSubscriptionStatus('idle');
      setErrorMessage('');
      
      // Call the newsletter API to subscribe
      await newsletterAPI.subscribe(email, name);
      
      // Reset form and show success message
      setEmail('');
      setName('');
      setSubscriptionStatus('success');
    } catch (err: any) {
      setSubscriptionStatus('error');
      setErrorMessage(err.response?.data?.message || 'Der opstod en fejl. Prøv igen senere.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-snudefryd-blue text-white py-12">
      <div className="container-custom">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Column 1: Logo and Description */}
          <div className="text-center sm:text-left">
            <h3 className="text-xl font-bold mb-4">Snudefryd</h3>
            <p className="text-gray-200 mb-4">
              Rådgiver i hundeernæring med fokus på vejledning til hundeejere.
            </p>
            <div className="mt-4">
              <p className="text-gray-200 mb-2">{CONTACT_INFO.email}</p>
              <p className="text-gray-200">{CONTACT_INFO.phone}</p>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="text-center sm:text-left">
            <h3 className="text-xl font-bold mb-4">Links</h3>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-gray-200 hover:text-white transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Social Media and Contact */}
          <div className="text-center sm:text-left">
            <h3 className="text-xl font-bold mb-4">Følg os</h3>
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center sm:justify-start text-gray-200 hover:text-white mb-4 transition-colors"
            >
              <svg
                className="w-6 h-6 mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              Instagram
            </a>
            
            {SOCIAL_LINKS.facebook && (
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center sm:justify-start text-gray-200 hover:text-white mb-4 transition-colors"
              >
                <svg
                  className="w-6 h-6 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Facebook
              </a>
            )}
          </div>

          {/* Column 4: Newsletter Subscription */}
          <div className="text-center sm:text-left">
            <h3 className="text-xl font-bold mb-4">Nyhedsbrev</h3>
            <p className="text-gray-200 mb-4">
              Tilmeld dig vores nyhedsbrev for at få de seneste nyheder og tips om hundeernæring.
            </p>
            
            {subscriptionStatus === 'success' ? (
              <div className="bg-green-800 text-white p-4 rounded-md">
                <p>Tak for din tilmelding! Du vil snart modtage vores nyhedsbrev.</p>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div>
                  <input
                    type="text"
                    placeholder="Dit navn (valgfrit)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-md bg-white text-black placeholder-gray-500 border border-gray-300 focus:outline-none focus:border-snudefryd-pink"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Din email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-md bg-white text-black placeholder-gray-500 border border-gray-300 focus:outline-none focus:border-snudefryd-pink"
                  />
                </div>
                {errorMessage && (
                  <p className="text-red-300 text-sm">{errorMessage}</p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={isSubmitting}
                  className="border border-white py-3"
                >
                  {isSubmitting ? 'Tilmelder...' : 'Tilmeld'}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-6 border-t border-gray-700 text-center text-gray-300">
          <p>© {currentYear} Snudefryd. Alle rettigheder forbeholdes.</p>
          <div className="mt-2 flex justify-center space-x-4 text-sm">
            <Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors">
              Privatlivspolitik
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 